import {
  action,
  computed,
  makeObservable,
  observable,
  ObservableMap,
  runInAction,
} from 'mobx'
import { ViewState } from 'react-map-gl/mapbox'

import {
  IDeliveryConfigurations,
  IGeoJson2DGeographicCoordinatesInput,
  ILogisticsConfigurations,
  IOrderedSitemap,
  ISitemapSpecificItemData,
  IWorkflowConfigurations,
  UploadingType,
} from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import { SCALED_IMAGE_CONFIG } from '~/client/src/shared/constants/scaledImage'
import { ISitemapsSetupSettings } from '~/client/src/shared/enums/ProjectSpecificUserProps'
import SitemapType from '~/client/src/shared/enums/SitemapType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Basemap from '~/client/src/shared/models/Basemap'
import IGeoPosition from '~/client/src/shared/models/IGeoPosition'
import Sitemap from '~/client/src/shared/models/Sitemap'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'
import GatesStore from '~/client/src/shared/stores/domain/Gates.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import ZonesStore from '~/client/src/shared/stores/domain/Zones.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import EventTypes from '~/client/src/shared/stores/EventStore/eventTypes'
import { NORTH_HEADING, WORLD_BOUNDS } from '~/client/src/shared/utils/Address'

import MapViewSetUpStore from '../MapViewSetUp.store'
import ISitemapDelayedAction, {
  ACTION_KEY,
  DelayedActionType,
  DTO_ID_KEY,
  NEW_SITEMAP_TYPE_KEY,
  SITEMAP_ID_KEY,
} from '../models/ISitemapDelayedAction'
import MapViewItemsSetupStore from './MapViewItemsSetup.store'
import { MapViewType } from './MapViewsList.store'

const UNTITLED = 'Untitled'

const DOT = '.'

export default class SitemapsSetupStore {
  @observable public viewport: IGeoPosition = {
    latitude: 0,
    longitude: 0,
    zoom: 0,
    bearing: NORTH_HEADING,
    pitch: 0,
    bounds: {
      ne: WORLD_BOUNDS._ne,
      sw: WORLD_BOUNDS._sw,
    },
  }
  @observable public newSitemapType: SitemapType = SitemapType.Default
  @observable public isSitemapUploading: boolean = false
  @observable public isSavingGeolocation: boolean = false

  @observable public deletableSitemap: Sitemap = null

  @observable public isAssignSitemapDialogShown: boolean = false
  // Anchor for the location-association popover (set by chip click).
  @observable.ref public assignDialogAnchor: HTMLElement | null = null

  @observable public editableSitemapName: string = ''
  @observable public pdfSitemapFile: File = null
  @observable private selectedSitemapId: string = null

  private delayedAction: ISitemapDelayedAction

  public constructor(
    private readonly eventsStore: DesktopEventStore,
    private readonly sitemapsStore: SitemapsStore,
    private readonly basemapsStore: BasemapsStore,
    private readonly fileUploadingStore: FileUploadingStore,
    private readonly mapViewSetUpStore: MapViewSetUpStore,
    private readonly gatesStore: GatesStore,
    private readonly zonesStore: ZonesStore,
    private readonly userProjectsStore: UserProjectsStore,
    private readonly globeViewsStore: GlobeViewsStore,
    private readonly locationAttributesStore: LocationAttributesStore,
  ) {
    makeObservable(this)
  }

  @computed
  public get allSitemaps(): Sitemap[] {
    return this.sitemapsStore.list.slice()
  }

  @computed
  public get whiteboards(): Sitemap[] {
    return this.sitemapsStore.list.filter(s => !s.isReferenced)
  }

  @computed
  public get defaultSitemaps(): Sitemap[] {
    return this.sitemapsStore.defaultSitemaps
  }

  @computed
  public get gateSitemaps(): Sitemap[] {
    return this.sitemapsStore.gateSitemaps
  }

  @computed
  public get zoneSitemaps(): Sitemap[] {
    return this.sitemapsStore.zoneSitemaps
  }

  @computed
  public get selectedSitemap(): Sitemap {
    return this.sitemapsStore.byId.get(this.selectedSitemapId)
  }

  @computed
  public get sitemapsMap(): ObservableMap<string, Sitemap> {
    return this.sitemapsStore.byId
  }

  @computed
  public get selectedBasemap(): Basemap {
    return (
      this.selectedSitemap &&
      this.basemapsStore.byId.get(this.selectedSitemap.basemapId)
    )
  }

  @action.bound
  public setViewportFromAddress(): void {
    const { projectAddress } = this.state

    this.viewport.bounds = this.selectedSitemap?.bounds || projectAddress.bounds
    this.viewport.zoom = this.selectedSitemap?.zoom || projectAddress.zoom
    this.viewport.altitude =
      this.selectedSitemap?.altitude || projectAddress.altitude
    this.viewport.pitch = this.selectedSitemap?.pitch || projectAddress.pitch
    this.viewport.bearing =
      this.selectedSitemap?.bearing || projectAddress.bearing
    this.viewport.latitude =
      this.selectedSitemap?.center?.lat || projectAddress.center?.lat
    this.viewport.longitude =
      this.selectedSitemap?.center?.lng || projectAddress.center?.lng
  }

  public setDelayedActionParamsFromUrl(query: URLSearchParams): void {
    const params: ISitemapDelayedAction = {
      action: query.get(ACTION_KEY) as DelayedActionType,
      newSitemapType: query.get(NEW_SITEMAP_TYPE_KEY) as SitemapType,
      dtoId: query.get(DTO_ID_KEY),
      sitemapId: query.get(SITEMAP_ID_KEY),
    }
    if (this.isDelayedActionParamsValid(params)) {
      this.delayedAction = params
    }
  }

  public isDelayedActionParamsValid(params: ISitemapDelayedAction): boolean {
    switch (params.action) {
      case DelayedActionType.SetDefault:
        return !!params.newSitemapType
      case DelayedActionType.AssignToGate:
      case DelayedActionType.AssignToZone:
        return !!params.newSitemapType && !!params.dtoId
      case DelayedActionType.SelectSitemap:
        return !!params.sitemapId
      default:
        return false
    }
  }

  @action.bound
  public selectInitialSitemap(): void {
    if (
      this.delayedAction &&
      this.delayedAction.action === DelayedActionType.SelectSitemap
    ) {
      const sitemap = this.sitemapsStore.byId.get(this.delayedAction.sitemapId)
      this.selectSitemap(sitemap)
      this.setLastEditedSitemap(sitemap.id)
      return
    }

    if (this.delayedAction) {
      this.selectUploadingNewSitemap(this.delayedAction.newSitemapType)
      return
    }
    if (this.lastEditedSitemap) {
      this.selectSitemap(this.lastEditedSitemap)
      return
    }
    if (this.defaultSitemap) {
      this.selectSitemap(this.defaultSitemap)
      this.setLastEditedSitemap(this.defaultSitemap.id)
      return
    }

    const sitemapsList = [
      ...this.sitemapsStore.defaultSitemaps,
      ...this.sitemapsStore.gateSitemaps,
      ...this.sitemapsStore.zoneSitemaps,
    ]

    const firstGlobe = this.globeViewsStore.list[0]
    if (sitemapsList[0]) {
      this.selectSitemap(sitemapsList[0])
      this.setLastEditedSitemap(sitemapsList[0].id)
    } else if (firstGlobe) {
      this.mapViewSetUpStore.selectMapView(MapViewType.GlobeView, firstGlobe.id)
    }
  }

  public get sitemapUrl(): string {
    return this.selectedBasemap && this.selectedBasemap.source
  }

  public get isSiteMapLabelsShown(): boolean {
    return this.selectedSitemap && this.selectedSitemap.isLabelsShown
  }

  @action.bound
  public async selectUploadingNewSitemap(type?: SitemapType): Promise<void> {
    await this.mapViewItemsSetupStore.saveSelectedMapViewItem()
    this.deselectSitemap()
    this.newSitemapType = type || SitemapType.Default
    this.editableSitemapName = UNTITLED
  }

  public updateGeoposition = async (
    sitemap: Sitemap,
    geoposition?: ViewState,
    width?: number,
    height?: number,
    bounds?: IGeoJson2DGeographicCoordinatesInput[],
  ): Promise<void> => {
    this.isSavingGeolocation = true
    if (geoposition) {
      sitemap.setGeoposition(geoposition, width, height, bounds)
    }

    await this.saveSitemap(sitemap, false, false, width, height)
    this.isSavingGeolocation = false
  }

  public uploadNewSitemap = async (
    file: File | Blob,
    name?: string,
    fileName?: string,
    projectId?: string,
    geoposition?: ViewState,
    width?: number,
    height?: number,
    bounds?,
  ): Promise<Sitemap> => {
    if (!file) {
      return
    }

    this.isSitemapUploading = true

    if (this.setPdf(file)) {
      return
    }

    const fullFileName =
      fileName ||
      (this.pdfSitemapFile ? this.pdfSitemapFile.name : this.getFileName(file))

    const basemapName = name || fullFileName.split(DOT).slice(0, -1).join(DOT)

    const sitemapUrl = await this.getImageUrl(file)

    const basemap = new Basemap(
      null,
      basemapName,
      projectId || this.state.activeProject.id,
      fullFileName,
      sitemapUrl,
    )
    const basemapId = await this.saveBasemap(basemap)

    const sitemap = new Sitemap(
      null,
      name?.trim().length ? name.trim() : fileName,
      projectId || this.state.activeProject.id,
      SitemapType.Default,
      basemapId,
      sitemapUrl,
      null,
      true,
      [],
    )

    if (geoposition) {
      sitemap.setGeoposition(geoposition, width, height, bounds)
    }
    sitemap.id = await this.saveSitemap(sitemap, false, false, width, height)

    if (sitemap.id) {
      this.runPostCreationAction(sitemap.id)
      this.selectSitemap(sitemap)
      this.setLastEditedSitemap(sitemap.id)
    }

    this.isSitemapUploading = false

    return sitemap
  }

  public uploadConvertedImage = async (imgFile: Blob): Promise<void> => {
    if (imgFile) {
      await this.uploadNewSitemap(imgFile)
    }

    this.pdfSitemapFile = null
  }

  public runPostCreationAction(sitemapId: string): void {
    if (!this.delayedAction) {
      return
    }
    const { action: actionType, dtoId } = this.delayedAction
    switch (actionType) {
      case DelayedActionType.SetDefault:
        const { maps } = this.state.logistics.configurations
        if (!maps.some(s => s.sitemapId === sitemapId)) {
          maps.push({
            sitemapId,
            order: maps.length,
          })

          this.eventsStore.dispatch(e.SAVE_LOGISTICS_CONFIGURATIONS, {
            maps,
          })
        }
        break
      case DelayedActionType.AssignToGate:
        const gate = this.gatesStore.byId.get(dtoId)
        if (gate) {
          gate.assignSitemap(sitemapId)
          this.gatesStore.saveItem(gate)
        }
        break
      case DelayedActionType.AssignToZone:
        const zone = this.zonesStore.byId.get(dtoId)
        if (zone) {
          zone.assignSitemap(sitemapId)
          this.zonesStore.saveItem(zone)
        }
        break
    }

    this.delayedAction = null
  }

  @action.bound
  public duplicateSitemap = async (sitemapId: string): Promise<void> => {
    this.isSitemapUploading = true
    const sitemap = this.allSitemaps.find(s => s.id === sitemapId)

    const newSitemap = Object.assign(sitemap.getCopy(), {
      id: null,
    })
    newSitemap.name = Localization.translator.duplicatedName(sitemap.name)

    newSitemap.id = await this.saveSitemap(newSitemap)

    if (newSitemap.id) {
      this.runPostCreationAction(newSitemap.id)
      await this.selectSitemap(newSitemap)
      this.setLastEditedSitemap(newSitemap.id)
    }

    this.isSitemapUploading = false
  }

  @action.bound
  public updateEditableSitemapName(value: string): void {
    this.editableSitemapName = value
  }

  public updateSitemapName = (sitemap: Sitemap, name: string): void => {
    const newSitemap = sitemap.getCopy()
    newSitemap.name = name || this.editableSitemapName
    this.editableSitemapName = ''

    this.saveSitemap(newSitemap)
  }

  public updateSelectedSitemapName = async (
    shouldDeselect?: boolean,
  ): Promise<void> => {
    if (
      !this.editableSitemapName ||
      this.selectedSitemap?.isNameEqual(this.editableSitemapName)
    ) {
      return
    }

    this.selectedSitemap.setName(this.editableSitemapName)

    await this.saveSitemapWithLoader(this.selectedSitemap)
    if (shouldDeselect) {
      this.deselectSitemap()
    }
  }

  private assignSitemapToProjectLocation = async (
    sitemapId: string,
  ): Promise<void> => {
    const newProjectLocation =
      this.locationAttributesStore.projectLocation.copy()
    newProjectLocation.assignedSitemaps.push(sitemapId)
    await this.locationAttributesStore.projectLocationsStore.saveItem(
      newProjectLocation,
    )
  }

  public async saveSitemap(
    sitemap: Sitemap,
    shouldApplyChangesBeforeSave?: boolean,
    avoidSaving?: boolean,
    width?: number,
    height?: number,
  ): Promise<string> {
    return new Promise<string>(resolve => {
      this.sitemapsStore.save(
        sitemap,
        async id => {
          if (
            !sitemap.isReferenced &&
            !this.locationAttributesStore.allAttributes.some(a =>
              a.isSitemapAssigned(id),
            )
          ) {
            // Settle assignment before resolving so a following selectSitemap lands last.
            try {
              await this.assignSitemapToProjectLocation(id)
            } catch {
              // A failed assignment must not block save resolution.
            }
          }
          resolve(id)
        },
        shouldApplyChangesBeforeSave,
        avoidSaving,
        width,
        height,
      )
    })
  }

  public saveSitemapItemData = async (
    items: ISitemapSpecificItemData[],
  ): Promise<string> => {
    return new Promise<string>(resolve => {
      this.sitemapsStore.saveSitemapItemData(
        items,
        id => resolve(id),
        true,
        false,
      )
    })
  }

  public addItemToSitemap = async (
    sitemapItemId: string,
    sitemapItemBase?: MapViewItemBase,
  ): Promise<string> => {
    const itemWithIndex = this.selectedSitemap.getItemDisplayData(sitemapItemId)
    if (sitemapItemBase) {
      const newItem = {
        ...sitemapItemBase.getDisplayData(false),
        id: itemWithIndex?.id,
        isHidden: false,
        isReferenced: false,
        sitemapId: this.selectedSitemap.id,
        projectId: this.selectedSitemap.projectId,
      } as ISitemapSpecificItemData
      return await this.saveSitemapItemData([newItem])
    } else {
      return await this.saveSitemapItemData([
        {
          id: itemWithIndex?.id,
          sitemapItemId,
          isHidden: false,
          sitemapId: this.selectedSitemap.id,
          projectId: this.selectedSitemap.projectId,
        },
      ])
    }
  }

  public deleteSitemapItemData = async (itemId: string): Promise<string> => {
    return new Promise<string>(resolve => {
      this.sitemapsStore.deleteSitemapItemData(
        itemId,
        id => resolve(id),
        true,
        false,
      )
    })
  }

  public saveBasemap(basemap: Basemap): Promise<string> {
    return new Promise<string>(resolve =>
      this.basemapsStore.save(basemap, id => resolve(id)),
    )
  }

  public uploadBaseMap = async (file: File | Blob): Promise<Basemap> => {
    if (!file) {
      return
    }

    this.isSitemapUploading = true

    const fullFileName = this.pdfSitemapFile
      ? this.pdfSitemapFile.name
      : this.getFileName(file)

    const fileName = fullFileName.split(DOT).slice(0, -1).join(DOT)

    const sitemapUrl = await this.getImageUrl(file)

    return new Basemap(
      null,
      fileName,
      this.state.activeProject.id,
      fileName,
      sitemapUrl,
    )
  }

  public updateBasemapForSitemap = async (
    file: File | Blob,
    sitemap: Sitemap,
    fileName?: string,
  ): Promise<void> => {
    this.mapViewSetUpStore.showLoader()

    const sitemapUrl = await this.getImageUrl(file)

    const basemap = new Basemap(
      null,
      fileName || this.getFileName(file),
      this.state.activeProject.id,
      fileName || this.getFileName(file),
      sitemapUrl,
    )

    basemap.id = await this.saveBasemap(basemap)

    sitemap.basemapId = basemap.id

    const filledImage = await this.createFilledImage(
      sitemapUrl,
      sitemap.itemsFilledImage,
    )

    sitemap.setFilledImage(filledImage)

    await this.saveSitemap(sitemap)

    this.mapViewSetUpStore.hideLoader()
  }

  public saveSitemapWithLoader = async (
    sitemap: Sitemap,
    shouldApplyChangesBeforeSave?: boolean,
  ): Promise<string> => {
    this.mapViewSetUpStore.showLoader()

    const id = await this.saveSitemap(sitemap, shouldApplyChangesBeforeSave)

    this.mapViewSetUpStore.hideLoader()

    return id
  }

  public toggleSitemapLabelsShown = async (): Promise<void> => {
    this.selectedSitemap.toggleLabelsShown()

    await this.saveSitemapWithLoader(this.selectedSitemap)
  }

  public updateSitemapFilledImage = async (
    sitemap: Sitemap,
    file: File,
    itemsFile: File,
  ): Promise<void> => {
    if (!this.selectedSitemap) {
      return
    }
    const sitemapUrl = await this.getImageUrl(file)
    const itemsUrl = await this.getImageUrl(itemsFile)

    sitemap.setFilledImage(sitemapUrl)
    sitemap.setItemsFilledImage(itemsUrl)

    await this.saveSitemap(sitemap)
  }

  @action.bound
  public async selectSitemapById(
    id: string,
    shouldSaveSelectedItem: boolean = true,
  ): Promise<void> {
    const sitemap = this.sitemapsStore.byId.get(id)
    this.selectSitemap(sitemap, shouldSaveSelectedItem)
  }

  @action.bound
  public async selectSitemap(
    sitemap: Sitemap,
    shouldSaveSelectedItem: boolean = true,
  ): Promise<void> {
    if (!sitemap) {
      return
    }

    if (shouldSaveSelectedItem) {
      await this.mapViewItemsSetupStore.saveSelectedMapViewItem()
    }

    this.deselectSitemap()

    runInAction(() => {
      this.selectedSitemapId = sitemap.id
      this.editableSitemapName = sitemap.name
    })
  }

  @action.bound
  public setLastEditedSitemap(lastEditedSitemapId: string): void {
    const { userActiveProjectSettings } = this.eventsStore.appState
    const userActiveProjectSettingsDto = userActiveProjectSettings.toDto()

    const sitemapSettings: ISitemapsSetupSettings = {
      lastEditedSitemapId,
    }

    userActiveProjectSettingsDto.sitemapsSetupSettings = sitemapSettings

    this.userProjectsStore.save([userActiveProjectSettingsDto])
  }

  @action.bound
  public deselectSitemap(): void {
    this.selectedSitemapId = null
    this.hideAssignSitemapDialog()
  }

  @action.bound
  public showDeleteConfirmationDialogForSelectedSitemap(): void {
    if (this.selectedSitemap) {
      this.showDeleteConfirmationDialogBySitemap(this.selectedSitemap)
    }
  }

  @action.bound
  public showDeleteConfirmationDialogBySitemap(sitemap: Sitemap): void {
    this.deletableSitemap = sitemap
  }

  @action.bound
  public showDeleteConfirmationDialogBySitemapId(sitemapId: string): void {
    this.deletableSitemap = this.allSitemaps.find(s => s.id === sitemapId)
  }

  @action.bound
  public hideSitemapDeleteConfirmDialog(): void {
    this.deletableSitemap = null
  }

  @action.bound
  public applySitemapDeleteConfirmDialog(): void {
    if (!this.deletableSitemap) {
      return
    }

    this.sitemapsStore.removeOne(this.deletableSitemap.id)

    this.hideSitemapDeleteConfirmDialog()

    this.selectInitialSitemap()
  }

  @action.bound
  public toggleAssignSitemapDialog(): void {
    this.isAssignSitemapDialogShown = !this.isAssignSitemapDialogShown
  }

  @action.bound
  public setAssignDialogAnchor(anchor: HTMLElement | null): void {
    this.assignDialogAnchor = anchor
  }

  @action.bound
  public showAssignSitemapDialog(): void {
    this.isAssignSitemapDialogShown = true
  }

  @action.bound
  public hideAssignSitemapDialog(): void {
    this.isAssignSitemapDialogShown = false
  }

  @computed
  public get deliveriesAssignedSitemaps(): { [filterType: string]: boolean } {
    return this.state.delivery.configurations.maps
      .filter(m => m.sitemapId)
      .map(m => m.sitemapId)
      .reduce((map, sitemapId) => {
        map[sitemapId] = true
        return map
      }, {})
  }

  @computed
  public get siteAssignedSitemaps(): { [filterType: string]: boolean } {
    return this.state.logistics.configurations.maps
      .filter(m => m.sitemapId)
      .map(m => m.sitemapId)
      .reduce((map, sitemapId) => {
        map[sitemapId] = true
        return map
      }, {})
  }

  @computed
  public get workflowsAssignedSitemaps(): { [filterType: string]: boolean } {
    return this.state.workflows.configurations.maps
      .filter(m => m.sitemapId)
      .map(m => m.sitemapId)
      .reduce((map, sitemapId) => {
        map[sitemapId] = true
        return map
      }, {})
  }

  private async createFilledImage(
    backgroundImage: string,
    itemsImage?: string,
  ): Promise<string> {
    if (!itemsImage) {
      return backgroundImage
    }

    const background = await this.loadImage(backgroundImage)
    const items = await this.loadImage(itemsImage)

    const { width, height } = background
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    ctx.canvas.width = width
    ctx.canvas.height = height

    ctx.drawImage(background, 0, 0, width, height)
    ctx.drawImage(items, 0, 0, width, height)

    const blob = await new Promise<Blob>(resolve => {
      canvas.toBlob(
        resolve,
        SCALED_IMAGE_CONFIG.mimeType,
        SCALED_IMAGE_CONFIG.quality,
      )
    })

    return this.getImageUrl(blob)
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise(resolve => {
      const image = new Image()
      image.setAttribute('crossOrigin', 'anonymous')
      image.src = src
      image.onload = () => {
        resolve(image)
      }
    })
  }

  @action.bound
  private clearDeliveriesSitemap(sitemap: Sitemap): void {
    const { configurations } = this.state.delivery
    this.removeSitemapFromConfig(
      configurations,
      e.SAVE_DELIVERY_CONFIGURATIONS,
      sitemap,
    )
  }

  @action.bound
  private clearWorkflowsSitemap(sitemap: Sitemap): void {
    const { configurations } = this.state.workflows
    this.removeSitemapFromConfig(
      configurations,
      e.SAVE_WORKFLOWS_CONFIGURATIONS,
      sitemap,
    )
  }

  @action.bound
  private clearSiteSitemap(sitemap: Sitemap): void {
    const { configurations } = this.state.logistics
    this.removeSitemapFromConfig(
      configurations,
      e.SAVE_LOGISTICS_CONFIGURATIONS,
      sitemap,
    )
  }

  @action.bound
  private addSitemapToConfig(
    maps: IOrderedSitemap[],
    eventType: EventTypes,
    sitemap: Sitemap,
  ): void {
    if (!sitemap) {
      return
    }

    const { id } = sitemap
    if (!maps.some(s => s.sitemapId === id)) {
      maps.push({
        sitemapId: id,
        order: maps.length,
      })

      this.eventsStore.dispatch(eventType, {
        maps,
        projectId: this.state.activeProject.id,
      })
    }
  }

  @action.bound
  private removeSitemapFromConfig(
    config:
      | IDeliveryConfigurations
      | IWorkflowConfigurations
      | ILogisticsConfigurations,
    eventType: EventTypes,
    sitemap: Sitemap,
  ): void {
    if (!sitemap) {
      return
    }

    const { id } = sitemap

    config.maps = config.maps.filter(s => s.sitemapId !== id)

    this.eventsStore.dispatch(eventType, {
      maps: config.maps,
    })
  }

  private get defaultSitemap(): Sitemap {
    const { maps } = this.state.logistics.configurations
    const sitemaps = maps.filter(m => m.sitemapId)
    return sitemaps.length && this.sitemapsStore.byId.get(sitemaps[0].sitemapId)
  }

  private get lastEditedSitemap(): Sitemap {
    const { sitemapsSetupSettings } = this.state.userActiveProjectSettings

    const lastEditedSitemapId = sitemapsSetupSettings?.lastEditedSitemapId

    if (!lastEditedSitemapId) {
      return null
    }

    if (this.sitemapsStore.byId.has(lastEditedSitemapId)) {
      return this.sitemapsStore.byId.get(lastEditedSitemapId)
    }
  }

  private get mapViewItemsSetupStore(): MapViewItemsSetupStore {
    return this.mapViewSetUpStore.mapViewItemsSetupStore
  }

  private get state(): DesktopInitialState {
    return this.eventsStore.appState
  }

  private setPdf(file: File | Blob): boolean {
    if (file.type === 'application/pdf') {
      this.pdfSitemapFile = file as File
      return true
    }

    return false
  }

  private getFileName(file: File | Blob): string {
    return (file as File).name || UNTITLED
  }

  public async getImageUrl(sourceFile: File | Blob): Promise<string> {
    const isImage = sourceFile.type.startsWith('image/')

    const fileType = isImage ? UploadingType.Image : UploadingType.Pdf
    const [result] = await this.fileUploadingStore.uploadFile(
      sourceFile,
      fileType,
      this.eventsStore.appState.activeProject.id,
      (sourceFile as File).name,
      true,
    )

    if (!result) {
      return
    }

    return result.fileURL
  }

  @action.bound
  public onSiteSitemapSectionClick(sitemap: Sitemap): void {
    if (this.siteAssignedSitemaps[sitemap.id]) {
      this.clearSiteSitemap(sitemap)
    } else {
      this.assignSiteSitemap(sitemap)
    }
  }

  @action.bound
  public onFormsSitemapSectionClick(sitemap: Sitemap): void {
    if (this.workflowsAssignedSitemaps[sitemap.id]) {
      this.clearWorkflowsSitemap(sitemap)
    } else {
      this.assignWorkflowsSitemap(sitemap)
    }
  }

  @action.bound
  public onDeliveriesSectionClick(sitemap: Sitemap): void {
    if (this.deliveriesAssignedSitemaps[sitemap.id]) {
      this.clearDeliveriesSitemap(sitemap)
    } else {
      this.assignDeliveriesSitemap(sitemap)
    }
  }

  @action.bound
  private assignWorkflowsSitemap(sitemap: Sitemap): void {
    const { maps } = this.state.workflows.configurations
    this.addSitemapToConfig(maps, e.SAVE_WORKFLOWS_CONFIGURATIONS, sitemap)
  }

  @action.bound
  private assignSiteSitemap(sitemap: Sitemap): void {
    const { maps } = this.state.logistics.configurations
    this.addSitemapToConfig(maps, e.SAVE_LOGISTICS_CONFIGURATIONS, sitemap)
  }

  @action.bound
  private assignDeliveriesSitemap(sitemap: Sitemap): void {
    const { maps } = this.state.delivery.configurations
    this.addSitemapToConfig(maps, e.SAVE_DELIVERY_CONFIGURATIONS, sitemap)
  }
}
